/**
 * Client de l'API de confidentialité — L9, exercice des droits CNDP.
 *
 * **Aucun chemin de ce module n'entre dans la liste blanche du service worker** (invariant 17).
 * Ce sont des réponses strictement personnelles : une seule mise en cache resservirait l'état de
 * confidentialité d'un compte à celui qui se connecte ensuite sur le même appareil — exactement le
 * scénario que l'invariant existe pour empêcher, appliqué à la donnée la plus sensible du produit.
 *
 * **Le téléchargement de l'archive ne passe pas par `appeler`** : la réponse n'est pas du JSON mais
 * un fichier, et il ne quitte jamais une session authentifiée. Aucun lien signé, aucun envoi par
 * e-mail — expédier l'intégralité des données d'une personne en pièce jointe serait le pire choix
 * possible (conception L9, § 2.4).
 */

import { appeler, jetonCourant } from './api'

export type NatureDemande = 'Export' | 'Deletion'
export type StatutDemande = 'Pending' | 'Processing' | 'Completed' | 'Failed' | 'Cancelled'
export type NatureConsentement = 'TermsOfService' | 'PrivacyPolicy' | 'MarketingEmails'

export type Demande = {
  id: string
  kind: NatureDemande
  status: StatutDemande
  requestedAt: string
  scheduledFor: string | null
  completedAt: string | null
  resultExpiresAt: string | null
  failureCode: string | null
}

export type Consentement = {
  type: NatureConsentement
  policyVersion: string
  grantedAt: string
  revokedAt: string | null
  isCurrentVersion: boolean
  isRevocable: boolean
}

export type EtatConfidentialite = {
  consents: Consentement[]
  currentPolicyVersion: string
  pendingExport: Demande | null
  pendingDeletion: Demande | null
  availableExport: Demande | null
  deletionScheduledFor: string | null
}

export function etatConfidentialite(): Promise<EtatConfidentialite> {
  return appeler<EtatConfidentialite>('/me/privacy')
}

export function demanderExport(): Promise<{ id: string }> {
  return appeler<{ id: string }>('/me/data-exports', { method: 'POST' })
}

export function programmerSuppression(): Promise<{ id: string }> {
  return appeler<{ id: string }>('/me/deletion', { method: 'POST' })
}

export function annulerSuppression(): Promise<void> {
  return appeler<void>('/me/deletion', { method: 'DELETE' })
}

export function accepterConsentement(type: NatureConsentement): Promise<void> {
  return appeler<void>('/me/consents', {
    method: 'POST',
    body: JSON.stringify({ type }),
  })
}

export function retirerConsentement(type: NatureConsentement): Promise<void> {
  return appeler<void>(`/me/consents/${type}`, { method: 'DELETE' })
}

/**
 * Télécharge l'archive et déclenche l'enregistrement du fichier.
 *
 * **Un `fetch` authentifié puis un objet URL, et non un `<a href>` direct.** Le lien nu partirait
 * sans en-tête `Authorization` : le serveur répondrait 401, et l'utilisateur verrait un écran
 * d'erreur sur le geste le plus sensible du produit.
 */
export async function telechargerExport(id: string): Promise<void> {
  const reponse = await fetch(`/api/v1/me/data-exports/${id}/download`, {
    headers: jetonCourant() === null ? {} : { Authorization: `Bearer ${jetonCourant()}` },
    credentials: 'same-origin',
  })

  if (!reponse.ok) {
    throw new Error('export.indisponible')
  }

  const blob = await reponse.blob()
  const url = URL.createObjectURL(blob)

  const lien = document.createElement('a')
  lien.href = url
  lien.download = `mes-donnees.zip`
  lien.click()

  // Sans révocation, le blob reste en mémoire jusqu'au rechargement de la page — et il contient
  // l'intégralité des données de la personne.
  URL.revokeObjectURL(url)
}
